import React, { useEffect, useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { X, Plus, Check, ChevronsUpDown, Clock, Pin, PinOff } from 'lucide-react';
import { resolveLabel } from '@/lib/fieldLabel';
import { api } from '@/lib/api';
import { getRecentInsights, togglePinInsight } from '@/lib/recentInsights';
import { getTerm } from '@/lib/branding';

// Shared select trigger styles for the small dense controls used in this panel
export const SMALL_TRIGGER = '!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5';
export const TINY_TRIGGER = '!h-5 !text-[8px] !rounded !bg-muted/30 !border-border/30';

export function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-[10px] text-muted-foreground truncate min-w-0">{label}</Label>
      <Switch className="flex-shrink-0" checked={!!checked} onCheckedChange={onChange} />
    </div>
  );
}

export function CommonChartConfig({ showGrid, setShowGrid, showLegend, setShowLegend, cfg, set, themeActive }) {
  return (
    <React.Fragment>
      <ToggleRow label="Grid Lines" checked={showGrid} onChange={setShowGrid} />
      <ToggleRow label="Legend" checked={showLegend} onChange={setShowLegend} />
      <ToggleRow label="Axis Titles" checked={cfg.showAxisTitles !== false} onChange={v => set('showAxisTitles', v)} />
      <div>
        <Label className="text-[8px] text-muted-foreground">Color Palette</Label>
        <Select value={cfg.palette || 'default'} onValueChange={v => set('palette', v)} disabled={themeActive}>
          <SelectTrigger className={SMALL_TRIGGER} data-testid="viz-color-palette-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default Blue</SelectItem>
            <SelectItem value="warm">Warm (Red/Orange)</SelectItem>
            <SelectItem value="cool">Cool (Teal/Cyan)</SelectItem>
            <SelectItem value="earth">Earth Tones</SelectItem>
            <SelectItem value="monochrome">Monochrome</SelectItem>
          </SelectContent>
        </Select>
        {themeActive && <p className="text-[8px] text-muted-foreground/70 mt-0.5 leading-tight" data-testid="viz-color-palette-theme-hint">Controlled by the dashboard Color Theme. Set the theme to “Default (from config)” to use a per-visual palette.</p>}
      </div>
    </React.Fragment>
  );
}

export function ReferenceLineInput({ refLineValue, setRefLineValue, refLineLabel, setRefLineLabel, parameters }) {
  return (
    <div>
      <Label className="text-[8px] text-muted-foreground">Reference Line</Label>
      <div className="flex gap-1 mt-0.5">
        <Input value={refLineValue} onChange={e => setRefLineValue(e.target.value)} placeholder="Value or $Param" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 flex-1" />
        {parameters && parameters.length > 0 && (
          <Select onValueChange={v => setRefLineValue('$' + v)}>
            <SelectTrigger className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 w-20"><SelectValue placeholder="Param" /></SelectTrigger>
            <SelectContent>{parameters.map(p => <SelectItem key={p.name} value={p.name}>${p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <Input value={refLineLabel} onChange={e => setRefLineLabel(e.target.value)} placeholder="Label (e.g. Target)" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5" />
    </div>
  );
}

export function TopNSection({ cfg, set, currentVisual, allFields }) {
  return (
    <div className="border-t border-border/20 pt-1.5 mt-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] text-muted-foreground truncate min-w-0">Top / Bottom N</Label>
        <Switch className="flex-shrink-0" checked={cfg.topN?.enabled || false} onCheckedChange={v => set('topN', { ...(cfg.topN || {}), enabled: v, n: cfg.topN?.n || 5, direction: cfg.topN?.direction || 'top', by: cfg.topN?.by || '' })} />
      </div>
      {cfg.topN?.enabled && (
        <div className="space-y-1.5 mt-1.5">
          <div className="flex gap-1.5 items-center">
            <Select value={cfg.topN?.direction || 'top'} onValueChange={v => set('topN', { ...cfg.topN, direction: v })}>
              <SelectTrigger className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 w-20"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="top">Top</SelectItem><SelectItem value="bottom">Bottom</SelectItem></SelectContent>
            </Select>
            <Input type="number" min={1} max={100} value={cfg.topN?.n || 5} onChange={e => set('topN', { ...cfg.topN, n: Number(e.target.value) || 5 })} className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 w-14" />
          </div>
          <div>
            <Label className="text-[8px] text-muted-foreground">Sort by measure</Label>
            <Select value={cfg.topN?.by || '_auto_'} onValueChange={v => set('topN', { ...cfg.topN, by: v === '_auto_' ? '' : v })}>
              <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_auto_">Auto (first measure)</SelectItem>
                {(currentVisual?.valueMeasures || []).map(m => (
                  <SelectItem key={m} value={m}>{resolveLabel(allFields, m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}

export function PeriodComparisonSection({ cfg, set, currentVisual, allFields }) {
  return (
    <div className="border-t border-border/20 pt-1.5 mt-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] text-muted-foreground truncate min-w-0">Period Comparison</Label>
        <Switch className="flex-shrink-0" checked={cfg.periodComparison?.enabled || false} onCheckedChange={v => set('periodComparison', { ...(cfg.periodComparison || {}), enabled: v })} />
      </div>
      {cfg.periodComparison?.enabled && (
        <div className="space-y-1.5 mt-1.5">
          <div>
            <Label className="text-[8px] text-muted-foreground">Time Field</Label>
            <Select value={cfg.periodComparison?.timeField || '_auto_'} onValueChange={v => set('periodComparison', { ...cfg.periodComparison, timeField: v === '_auto_' ? '' : v })}>
              <SelectTrigger className={SMALL_TRIGGER}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_auto_">Select field...</SelectItem>
                {[...(currentVisual?.rowDims || []), ...(currentVisual?.filterDims || [])].map(d => (
                  <SelectItem key={d} value={d}>{resolveLabel(allFields, d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-1.5">
            <div className="flex-1">
              <Label className="text-[8px] text-muted-foreground">Current</Label>
              <Input value={cfg.periodComparison?.currentValue || ''} onChange={e => set('periodComparison', { ...cfg.periodComparison, currentValue: e.target.value })} placeholder="e.g. 2024" className={SMALL_TRIGGER} />
            </div>
            <div className="flex-1">
              <Label className="text-[8px] text-muted-foreground">Prior</Label>
              <Input value={cfg.periodComparison?.priorValue || ''} onChange={e => set('periodComparison', { ...cfg.periodComparison, priorValue: e.target.value })} placeholder="e.g. 2023" className={SMALL_TRIGGER} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DrillThroughSection({ cfg, set }) {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pinsRev, setPinsRev] = useState(0); // bump to re-read pinned state after toggle
  const enabled = cfg.drillThrough?.enabled || false;

  useEffect(() => {
    if (!enabled || targets.length > 0 || loading) return;
    setLoading(true);
    Promise.all([
      api.getReports().catch(() => []),
      api.getDashboards().catch(() => []),
    ]).then(([reports, dashboards]) => {
      const asItems = (list, kind) => (list || []).map(r => ({
        id: r.report_id || r.dashboard_id || r.id,
        name: r.name || r.title || '(untitled)',
        kind,
      })).filter(x => x.id);
      const combined = [...asItems(reports, 'Report'), ...asItems(dashboards, 'Dashboard')]
        .sort((a, b) => a.name.localeCompare(b.name));
      setTargets(combined);
    }).finally(() => setLoading(false));
  }, [enabled, targets.length, loading]);

  const current = cfg.drillThrough?.targetReportId || '';
  const currentTarget = useMemo(
    () => targets.find(t => t.id === current),
    [targets, current]
  );

  return (
    <div className="border-t border-border/20 pt-1.5 mt-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] text-muted-foreground truncate min-w-0">Drill-Through</Label>
        <Switch className="flex-shrink-0" checked={enabled} onCheckedChange={v => set('drillThrough', { ...(cfg.drillThrough || {}), enabled: v })} />
      </div>
      {enabled && (
        <div className="mt-1.5">
          <Label className="text-[8px] text-muted-foreground">Target Insight</Label>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between h-7 px-2 rounded bg-muted/40 border border-border/40 text-[10px] hover:bg-muted/60"
                data-testid="drill-through-target"
              >
                <span className="truncate text-left">
                  {loading ? (
                    <span className="text-muted-foreground/70">Loading insights…</span>
                  ) : currentTarget ? (
                    <>
                      <span className="text-muted-foreground/70 mr-1">{currentTarget.kind}:</span>
                      {currentTarget.name}
                    </>
                  ) : (
                    <span className="text-muted-foreground/80">Choose an insight to drill into</span>
                  )}
                </span>
                <ChevronsUpDown className="w-3 h-3 opacity-60 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0" data-testid="drill-through-popover">
              <Command>
                <CommandInput placeholder={`Search ${getTerm('insights', 'plural', 'lower')}…`} className="h-8 text-[11px]" data-testid="drill-through-search" />
                <CommandList className="max-h-64">
                  <CommandEmpty>
                    <span className="text-[10px] text-muted-foreground italic">
                      {targets.length === 0 ? `No ${getTerm('insights', 'plural', 'lower')} found in this tenant.` : 'No matches for that search.'}
                    </span>
                  </CommandEmpty>
                  {/* Recently viewed — pinned above the two group headings
                      so users can one-tap-link to insights they had open.
                      Pinned entries always render first (and never age out). */}
                  {(() => {
                    void pinsRev; // re-read after pin toggle
                    const recent = getRecentInsights(3)
                      .map(r => {
                        const t = targets.find(x => x.id === r.id);
                        return t ? { ...t, pinned: r.pinned } : r;
                      })
                      .filter(r => r && r.id);
                    if (!recent.length) return null;
                    const pinnedItems = recent.filter(r => r.pinned);
                    const unpinnedItems = recent.filter(r => !r.pinned);
                    const renderItem = (t) => (
                      <CommandItem
                        key={`recent-${t.id}`}
                        value={`recent ${t.name || ''} ${t.id}`}
                        onSelect={() => {
                          set('drillThrough', { ...cfg.drillThrough, targetReportId: t.id });
                          setPopoverOpen(false);
                        }}
                        className="text-[10px] cursor-pointer group/rec"
                        data-testid={`drill-through-recent-${t.id}`}
                      >
                        {t.pinned
                          ? <Pin className="w-3 h-3 mr-1.5 text-amber-400 shrink-0 fill-amber-400/40" />
                          : <Clock className="w-3 h-3 mr-1.5 text-indigo-400/80 shrink-0" />}
                        <span className="truncate flex-1">{t.name || t.id}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            togglePinInsight({ id: t.id, name: t.name, kind: t.kind });
                            setPinsRev(v => v + 1);
                          }}
                          className={`ml-1 p-0.5 rounded transition-opacity ${t.pinned ? 'opacity-100 text-amber-400 hover:text-amber-300' : 'opacity-0 group-hover/rec:opacity-100 text-muted-foreground/70 hover:text-foreground'}`}
                          title={t.pinned ? 'Unpin' : 'Pin to top'}
                          aria-label={t.pinned ? `Unpin ${t.name || t.id}` : `Pin ${t.name || t.id}`}
                          data-testid={t.pinned ? `drill-through-unpin-${t.id}` : `drill-through-pin-${t.id}`}
                        >
                          {t.pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                        </button>
                        <span className="text-[9px] text-muted-foreground/80 ml-2">{t.kind || 'Report'}</span>
                      </CommandItem>
                    );
                    return (
                      <>
                        {pinnedItems.length > 0 && (
                          <CommandGroup heading="Pinned" data-testid="drill-through-pinned-group">
                            {pinnedItems.map(renderItem)}
                          </CommandGroup>
                        )}
                        {unpinnedItems.length > 0 && (
                          <CommandGroup heading="Recently viewed">
                            {unpinnedItems.map(renderItem)}
                          </CommandGroup>
                        )}
                      </>
                    );
                  })()}
                  {['Report', 'Dashboard'].map(kind => {
                    const bucket = targets.filter(t => t.kind === kind);
                    if (!bucket.length) return null;
                    return (
                      <CommandGroup key={kind} heading={`${kind}s`}>
                        {bucket.map(t => (
                          <CommandItem
                            key={t.id}
                            value={`${t.kind} ${t.name} ${t.id}`}
                            onSelect={() => {
                              set('drillThrough', { ...cfg.drillThrough, targetReportId: t.id });
                              setPopoverOpen(false);
                            }}
                            className="text-[10px] cursor-pointer"
                            data-testid={`drill-through-target-${t.id}`}
                          >
                            <Check className={`w-3 h-3 mr-1.5 ${current === t.id ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="truncate flex-1">{t.name}</span>
                            <span className="text-[9px] text-muted-foreground/80 ml-2 font-mono">{t.id.slice(0, 12)}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {current && (
            <button
              onClick={() => set('drillThrough', { ...cfg.drillThrough, targetReportId: '' })}
              className="text-[8px] text-muted-foreground hover:text-destructive mt-0.5"
              data-testid="drill-through-clear"
            >Clear selection</button>
          )}
          <p className="text-[8px] text-muted-foreground/70 mt-0.5">Clicking a data point navigates to this insight, filtered by the clicked value.</p>
        </div>
      )}
    </div>
  );
}

// Palette used by ChartRenderer for series colors — kept in sync so the
// pickers preview the same default color a series would get automatically.
const SERIES_PALETTE = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#a855f7', '#14b8a6', '#f97316'];

export function SeriesSection({ cfg, set, seriesValues = [] }) {
  const topN = cfg.seriesTopN || {};
  const colors = cfg.seriesColors || {};
  const setColor = (val, hex) => set('seriesColors', { ...colors, [val]: hex });
  const clearColor = (val) => { const next = { ...colors }; delete next[val]; set('seriesColors', next); };
  return (
    <div className="border-t border-border/20 pt-1.5 mt-1.5" data-testid="series-section">
      <p className="text-[8px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Series (Color / Legend)</p>

      {/* Top N + Other */}
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] text-muted-foreground truncate min-w-0">Limit to Top N + Other</Label>
        <Switch className="flex-shrink-0" data-testid="series-topn-toggle"
          checked={!!topN.enabled}
          onCheckedChange={v => set('seriesTopN', { ...topN, enabled: v, count: topN.count || 8 })} />
      </div>
      {topN.enabled && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <Label className="text-[8px] text-muted-foreground">Keep top</Label>
          <Input type="number" min={1} max={50} data-testid="series-topn-count"
            value={topN.count || 8}
            onChange={e => set('seriesTopN', { ...topN, count: Math.max(1, Number(e.target.value) || 8) })}
            className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 w-14" />
          <Label className="text-[8px] text-muted-foreground">series, rest → “Other”</Label>
        </div>
      )}

      {/* Per-series color lock */}
      {seriesValues.length > 0 && (
        <div className="mt-2">
          <Label className="text-[8px] text-muted-foreground">Lock colors per series</Label>
          <div className="mt-1 flex flex-col gap-1 max-h-40 overflow-auto pr-0.5" data-testid="series-color-list">
            {seriesValues.map((val, i) => {
              const active = colors[val];
              const preview = active || SERIES_PALETTE[i % SERIES_PALETTE.length];
              return (
                <div key={val} className="flex items-center gap-1.5" data-testid={`series-color-row-${val}`}>
                  <input type="color" value={preview}
                    onChange={e => setColor(val, e.target.value)}
                    className="w-5 h-5 rounded border border-border/40 bg-transparent cursor-pointer p-0"
                    data-testid={`series-color-input-${val}`}
                    title={`Color for ${val}`} />
                  <span className="text-[9px] text-foreground/85 truncate flex-1" title={val}>{val}</span>
                  {active && (
                    <button onClick={() => clearColor(val)} title="Reset to auto"
                      className="text-muted-foreground/70 hover:text-destructive" data-testid={`series-color-reset-${val}`}>
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function PriorYearSection({ cfg, set }) {
  // "vs same point last year" comparison — shows only when the visual uses a
  // to-date measure (the backend attaches prior values then). Default ON.
  return (
    <div className="border-t border-border/20 pt-1.5 mt-1.5" data-testid="prior-year-section">
      <ToggleRow
        label="Prior year (vs same point last year)"
        checked={cfg.showPriorPeriod !== false}
        onChange={v => set('showPriorPeriod', v)}
      />
      <p className="text-[8px] text-muted-foreground/70 mt-0.5">Shows a “vs same point last year” delta on to-date KPIs, table cells and chart tooltips.</p>
    </div>
  );
}

export function ComputedColumnsSection({ cfg, set }) {
  return (
    <div className="border-t border-border/20 pt-1.5 mt-1.5">
      <p className="text-[8px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Computed Columns</p>
      {(cfg.computedColumns || []).map((cc, i) => (
        <div key={`cc-${i}`} className="flex items-center gap-1 mb-1">
          <Input value={cc.name} onChange={e => { const cols = [...(cfg.computedColumns || [])]; cols[i] = { ...cc, name: e.target.value }; set('computedColumns', cols); }} placeholder="Name" className="!h-5 !text-[8px] !rounded !bg-muted/30 !border-border/30 w-20" />
          <Input value={cc.formula} onChange={e => { const cols = [...(cfg.computedColumns || [])]; cols[i] = { ...cc, formula: e.target.value }; set('computedColumns', cols); }} placeholder="e.g. field_a - field_b" className="!h-5 !text-[8px] !rounded !bg-muted/30 !border-border/30 flex-1 font-mono" />
          <button onClick={() => set('computedColumns', (cfg.computedColumns || []).filter((_, j) => j !== i))} className="text-muted-foreground/30 hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
        </div>
      ))}
      <button onClick={() => set('computedColumns', [...(cfg.computedColumns || []), { name: '', formula: '' }])} className="text-[8px] text-primary hover:text-primary/80 flex items-center gap-0.5"><Plus className="w-2.5 h-2.5" /> Add Column</button>
    </div>
  );
}
